import { useMemo } from 'react'
import { useFleet } from 'hooks/fleet/useFleet'
import { useFleetCredentials } from 'hooks/fleet/useFleetCredentials'
import type { VoiceClient, VoiceClientContext } from '../types'

const CAPABILITIES = { sharedSession: true }

// React Mobile's fleet: the socket URL and a short-lived voice token come
// from the org's `voice` block on /users/current, refetched before expiry.
export function useFleetAdapter(
  { enabled, orgId, currentUser, language }: VoiceClientContext,
  refetchCurrentUser: () => Promise<unknown>,
): VoiceClient {
  const voice = useMemo(() => {
    if (!enabled) return null
    const org = currentUser?.orgs?.find(o => String(o?.id) === String(orgId))
    const block = org?.voice
    return block?.backend === 'fleet' && block.url && block.token ? block : null
  }, [enabled, currentUser, orgId])

  const credentials = useFleetCredentials(voice, refetchCurrentUser)

  const fleet = useFleet({
    url: voice?.url ?? null,
    getToken: credentials.getToken,
    subjectId: voice?.subjectId ?? null,
    language,
    onAuthTrouble: credentials.refresh,
  })

  const ready = Boolean(voice && currentUser?.id)

  return useMemo(() => ({ ...fleet, backend: 'fleet' as const, ready, capabilities: CAPABILITIES }), [fleet, ready])
}
